import { useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import FetchWithGif from '../../../Reutilizables/FetchWithGif';
import logo from '../../../../img/logo/Logo_FICSULLANA.png';
import { getBase64FromImage } from '../../../../utilities/pdfLogo';

const CONSTANTES = {
    ORIENTACION: 'landscape',
    UNIDAD: 'mm',
    FORMATO: 'a4',
    MARGENES: {
        PREDETERMINADO: 15,
        TABLA: 15
    },
    LOGO: {
        ANCHO: 50,
        ALTO: 48,
        POSICION_Y: 9
    },
    COLORES: {
        ROJO_PRIMARIO: [230, 0, 0],
        NEGRO: [44, 62, 80],
        BLANCO: [255, 255, 255],
        GRIS_100: [150, 150, 150],
        GRIS_60: [108, 122, 137],
        GRIS_CLARO: [230, 230, 230]
    },
    FUENTES: {
        TAMANIOS: {
            TITULO: 20,
            SUBTITULO: 13,
            NORMAL: 10,
            PEQUENIO: 9,
            MUY_PEQUENIO: 8
        }
    },
    TABLA: {
        ENCABEZADOS: [
            '#',
            'Cliente',
            'DNI',
            'Estado',
            'Último Préstamo',
            'Tasa %',
            'Última Cuota Pagada',
            'Teléfono',
            'Dirección'
        ],
        ESTILOS_COLUMNAS: {
            0: { cellWidth: 9 },
            1: { cellWidth: 35 },
            2: { cellWidth: 22 },
            3: { cellWidth: 28 },
            4: { cellWidth: 25 },
            5: { cellWidth: 15 },
            6: { cellWidth: 30 },
            7: { cellWidth: 22 },
            8: { cellWidth: 'auto' }
        }
    }
};

const ReporteClientesSinPrestamo = ({
    clientes = [],
    filters = {},
    estadisticas = {},
    onComplete,
    onPDFGenerated
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [logoBase64Cache, setLogoBase64Cache] = useState(null);

    const establecerFuenteDocumento = (doc, estilo, tamanio) => {
        doc.setFont('helvetica', estilo);
        doc.setFontSize(tamanio);
    };

    const establecerColorTexto = (doc, color) => {
        doc.setTextColor(...color);
    };

    const formatearFecha = (fecha) => {
        try {
            return format(new Date(fecha), 'dd/MM/yyyy');
        } catch {
            return format(new Date(), 'dd/MM/yyyy');
        }
    };

    const agregarLogoFallback = (doc, anchoPagina) => {
        const logoAncho = 45;
        const logoAlto = 50;
        const logoX = (anchoPagina - logoAncho) / 2;

        doc.setFillColor(...CONSTANTES.COLORES.BLANCO);
        doc.setDrawColor(...CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.setLineWidth(2);
        doc.roundedRect(logoX, 8, logoAncho, logoAlto, 3, 3, 'FD');

        establecerFuenteDocumento(doc, 'bold', 11);
        establecerColorTexto(doc, CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.text('FICS', logoX + logoAncho / 2, 28, { align: 'center' });
        doc.text('ULLANA', logoX + logoAncho / 2, 38, { align: 'center' });
    };

    const agregarLogo = async (doc) => {
        try {
            if (!logoBase64Cache) {
                const logoBase64 = await getBase64FromImage(logo);
                setLogoBase64Cache(logoBase64);
            }

            doc.addImage(
                logoBase64Cache || await getBase64FromImage(logo),
                'PNG',
                CONSTANTES.MARGENES.PREDETERMINADO,
                CONSTANTES.LOGO.POSICION_Y,
                CONSTANTES.LOGO.ANCHO,
                CONSTANTES.LOGO.ALTO
            );
        } catch (error) {
            console.warn('No se pudo cargar el logo, usando fallback');
            const anchoPagina = doc.internal.pageSize.getWidth();
            agregarLogoFallback(doc, anchoPagina);
        }
    };

    const agregarEncabezado = (doc, anchoPagina) => {
        doc.setLineWidth(3);
        doc.setDrawColor(...CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.line(0, 0, anchoPagina, 0);

        const textoX = anchoPagina - CONSTANTES.MARGENES.PREDETERMINADO;
        const encabezadoY = 38;
        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.TITULO);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
        doc.text('REPORTE DE CLIENTES SIN PRÉSTAMO ACTIVO', textoX, encabezadoY + 6, { align: 'right' });
    };

    const dibujarSeccion = (doc, titulo, y, anchoPagina, altoPagina) => {
        const altoSeccion = 6;
        if (y + altoSeccion + 5 > altoPagina - 50) {
            doc.addPage();
            y = 20;
        }

        doc.setFillColor(...CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.rect(
            CONSTANTES.MARGENES.PREDETERMINADO,
            y,
            anchoPagina - 2 * CONSTANTES.MARGENES.PREDETERMINADO,
            altoSeccion,
            'F'
        );

        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.SUBTITULO);
        establecerColorTexto(doc, CONSTANTES.COLORES.BLANCO);
        doc.text(titulo, CONSTANTES.MARGENES.PREDETERMINADO + 5, y + 4.5);

        return y + altoSeccion + 3;
    };

    const obtenerInformacionFiltros = () => {
        const filtrosInfo = [];

        if (filters.search) {
            filtrosInfo.push(`Búsqueda: ${filters.search}`);
        }

        if (filters.estado_cliente && filters.estado_cliente !== 'todos') {
            const estadoTexto = filters.estado_cliente === 'sin_prestamo_activo' 
                ? 'Con historial de préstamos' 
                : 'Sin registro de préstamos';
            filtrosInfo.push(`Estado: ${estadoTexto}`);
        }

        if (filtrosInfo.length === 0) {
            filtrosInfo.push('Sin filtros aplicados - Mostrando todos los clientes sin préstamo activo');
        }

        return filtrosInfo;
    };

    const agregarSeccionFiltros = (doc, y) => {
        const filtrosInfo = obtenerInformacionFiltros();

        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);

        filtrosInfo.forEach((info) => {
            doc.text(`• ${info}`, CONSTANTES.MARGENES.PREDETERMINADO + 5, y);
            y += 5;
        });

        return y + 3;
    };

    const agregarResumen = (doc, y) => {
        const { total_clientes_sin_prestamo_activo = 0, con_historial_prestamos = 0, sin_registro_prestamos = 0 } = estadisticas;

        establecerFuenteDocumento(doc, 'bold', 11);
        establecerColorTexto(doc, CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.text(
            `Total de clientes sin préstamo activo: ${total_clientes_sin_prestamo_activo}`,
            CONSTANTES.MARGENES.PREDETERMINADO + 5,
            y
        );

        y += 5;
        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
        doc.text(
            `Con historial de préstamos: ${con_historial_prestamos}`,
            CONSTANTES.MARGENES.PREDETERMINADO + 5,
            y
        );

        y += 4;
        doc.text(
            `Sin registro de préstamos: ${sin_registro_prestamos}`,
            CONSTANTES.MARGENES.PREDETERMINADO + 5,
            y
        );

        y += 4;
        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        doc.text(
            `Registros en este reporte: ${clientes.length}`,
            CONSTANTES.MARGENES.PREDETERMINADO + 5,
            y
        );

        return y + 8;
    };

    const formatearDatosTabla = () => {
        return clientes.map((cliente, index) => {
            const estadoTexto = cliente.estado_cliente === 'sin_prestamo_activo' 
                ? 'Con Historial' 
                : 'Sin Registro';

            const ultimoPrestamo = cliente.ultimo_prestamo?.monto_prestamo 
                ? `S/ ${parseFloat(cliente.ultimo_prestamo.monto_prestamo).toFixed(2)}`
                : '—';

            const tasaInteres = cliente.ultimo_prestamo?.tasa_interes 
                ? `${parseFloat(cliente.ultimo_prestamo.tasa_interes).toFixed(2)}%`
                : '—';

            const ultimaCuota = cliente.ultimo_prestamo?.ultima_cuota_pagada?.fecha_pago
                ? formatearFecha(cliente.ultimo_prestamo.ultima_cuota_pagada.fecha_pago)
                : '—';

            return [
                (index + 1).toString(),
                cliente.nombre_completo || '—',
                cliente.datos?.numero_documento || '—',
                estadoTexto,
                ultimoPrestamo,
                tasaInteres,
                ultimaCuota,
                cliente.contacto?.telefono || '—',
                cliente.direccion_completa || '—'
            ];
        });
    };

    const agregarTabla = (doc, y, anchoPagina, altoPagina) => {
        const datos = formatearDatosTabla();
        const fechaActual = formatearFecha(new Date());

        autoTable(doc, {
            head: [CONSTANTES.TABLA.ENCABEZADOS],
            body: datos,
            startY: y,
            margin: {
                left: CONSTANTES.MARGENES.TABLA,
                right: CONSTANTES.MARGENES.TABLA
            },
            tableWidth: 'auto',
            theme: 'striped',
            headStyles: {
                fillColor: CONSTANTES.COLORES.ROJO_PRIMARIO,
                textColor: CONSTANTES.COLORES.BLANCO,
                fontStyle: 'bold',
                fontSize: CONSTANTES.FUENTES.TAMANIOS.PEQUENIO,
                halign: 'center',
                valign: 'middle'
            },
            styles: {
                fontSize: CONSTANTES.FUENTES.TAMANIOS.MUY_PEQUENIO,
                cellPadding: 2,
                valign: 'middle',
                overflow: 'linebreak'
            },
            bodyStyles: {
                textColor: CONSTANTES.COLORES.NEGRO,
                halign: 'center'
            },
            columnStyles: CONSTANTES.TABLA.ESTILOS_COLUMNAS,
            didDrawPage: () => {
                const numeroPaginas = doc.internal.getNumberOfPages();
                const paginaActual = doc.internal.getCurrentPageInfo().pageNumber;

                establecerFuenteDocumento(doc, 'italic', CONSTANTES.FUENTES.TAMANIOS.MUY_PEQUENIO);
                establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_100);
                doc.text(
                    `Página ${paginaActual} de ${numeroPaginas}`,
                    anchoPagina / 2,
                    altoPagina - 10,
                    { align: 'center' }
                );
            }
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        establecerFuenteDocumento(doc, 'italic', CONSTANTES.FUENTES.TAMANIOS.PEQUENIO);
        establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_100);
        doc.text(
            `Fecha de emisión: ${fechaActual}`,
            anchoPagina / 2,
            finalY + 3,
            { align: 'center' }
        );
    };

    const generarPDF = async () => {
        if (isGenerating) return;

        setIsGenerating(true);

        try {
            const doc = new jsPDF({
                orientation: CONSTANTES.ORIENTACION,
                unit: CONSTANTES.UNIDAD,
                format: CONSTANTES.FORMATO,
                compress: true
            });

            const anchoPagina = doc.internal.pageSize.getWidth();
            const altoPagina = doc.internal.pageSize.getHeight();
            let y = CONSTANTES.MARGENES.PREDETERMINADO;

            agregarEncabezado(doc, anchoPagina);
            await agregarLogo(doc);
            y += 45;
            y = dibujarSeccion(doc, 'FILTROS APLICADOS', y, anchoPagina, altoPagina);
            y += 3;
            y = agregarSeccionFiltros(doc, y);
            y = agregarResumen(doc, y);
            y = dibujarSeccion(doc, 'LISTADO DE CLIENTES', y, anchoPagina, altoPagina);
            agregarTabla(doc, y, anchoPagina, altoPagina);

            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);

            if (onPDFGenerated) {
                onPDFGenerated(pdfUrl);
            }

            toast.success('PDF generado correctamente');
            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error('Error al generar PDF:', error);
            toast.error('Error al generar el PDF');

            if (onComplete) {
                onComplete();
            }
        } finally {
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        generarPDF();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return isGenerating ? <FetchWithGif /> : null;
};

export default ReporteClientesSinPrestamo;
